import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, EyeOff, Loader2, Plus, Trash2 } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SampleBadge, StatusBadge } from "@/components/site/Badges";
import { AdminLayout, btnPrimary, btnSecondary, ConfirmDelete, FieldError, inputCls, Panel } from "@/components/admin/AdminLayout";

export const StatusToggle = ({ value, onChange, prefix }) => (
  <div className="flex gap-2">
    {["In Progress", "Completed"].map((s) => (
      <button
        key={s}
        type="button"
        onClick={() => onChange(s)}
        className={`h-12 flex-1 rounded-md border text-base font-medium transition-colors ${value === s ? "border-[#FFD23F] bg-[#FFD23F] text-[#121214]" : "border-zinc-700 text-zinc-200 hover:bg-zinc-800"}`}
        data-testid={`${prefix}-status-${s.toLowerCase().replace(" ", "-")}`}
      >
        {s}
      </button>
    ))}
  </div>
);

const NewProject = () => {
  const navigate = useNavigate();
  const [f, setF] = useState({ title: "", status: "In Progress", description: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/projects", f);
      toast.success("Project created — now add photos & videos");
      navigate(`/admin/projects/${data.id}`);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <Panel title="New project" testId="new-project-panel">
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="new-project-form">
        <div>
          <Label htmlFor="np-title">Title / Make &amp; Model <span className="text-zinc-500">(optional)</span></Label>
          <Input id="np-title" placeholder="e.g. 1969 Camaro — leave blank to just show photos" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="new-project-title-input" />
          <FieldError msg={error} testId="new-project-title-error" />
        </div>
        <StatusToggle value={f.status} onChange={(status) => setF({ ...f, status })} prefix="new-project" />
        <Textarea placeholder="Description (optional)" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-[#121214] border-zinc-700 text-base" data-testid="new-project-description-input" />
        <Button type="submit" disabled={busy} className={`${btnPrimary} w-full sm:w-auto`} data-testid="new-project-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create project
        </Button>
      </form>
    </Panel>
  );
};

const Row = ({ p }) => (
  <Link to={`/admin/projects/${p.id}`} className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-[#1A1A1E] p-3 transition-colors hover:border-[#FFD23F]/60" data-testid={`admin-project-row-${p.id}`}>
    <div className="h-16 w-20 shrink-0 overflow-hidden rounded bg-[#121214]">
      {p.cover?.url ? <img src={mediaUrl(p.cover.url)} alt="" className="h-full w-full object-cover" /> : <Camera className="m-auto mt-5 h-6 w-6 text-zinc-600" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-base font-semibold text-zinc-100" data-testid="admin-project-row-title">{p.title || "Untitled project"}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <StatusBadge status={p.status} />
        {p.is_sample && <SampleBadge />}
        {!p.visible && <span className="inline-flex items-center gap-1 rounded-sm bg-zinc-800 px-2 py-1 text-xs text-zinc-300" data-testid="admin-project-hidden-badge"><EyeOff className="h-3 w-3" /> Hidden</span>}
        {p.processing_count > 0 && <span className="text-xs text-[#FFD23F]">Processing…</span>}
      </div>
      {p.photo_count + p.video_count === 0 && <p className="mt-1.5 text-xs text-[#FFD23F]" data-testid="admin-project-no-media-hint">Add photos or videos — this project is hidden from your site until it has at least one</p>}
    </div>
  </Link>
);

export default function ProjectsPage() {
  const [reload, setReload] = useState(0);
  const { data } = useFetch(() => api.get("/admin/projects"), [reload]);
  const samples = (data || []).filter((p) => p.is_sample).length;
  const removeSamples = async () => {
    try {
      const { data: r } = await api.post("/admin/samples/remove");
      toast.success(`Removed ${r.removed} sample projects`);
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <AdminLayout title="Projects" testId="admin-projects-page">
      <NewProject />
      <h2 className="font-sub mb-3 text-lg font-semibold uppercase tracking-wide text-[#FAF7F2]">Your projects</h2>
      {!data ? (
        <p className="text-zinc-400">Loading…</p>
      ) : data.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-700 p-8 text-center text-zinc-400" data-testid="admin-projects-empty">No projects yet. Create your first one above.</p>
      ) : (
        <div className="space-y-2" data-testid="admin-project-list">{data.map((p) => <Row key={p.id} p={p} />)}</div>
      )}
      {samples > 0 && (
        <Panel className="mt-8 border-dashed" testId="sample-content-panel">
          <p className="mb-3 text-sm text-zinc-300">{samples} sample projects are showing on your site with a “Sample” badge. Remove them once you've posted your own work.</p>
          <ConfirmDelete
            description="All sample projects and their photos will be deleted."
            confirmLabel="Yes, remove samples"
            onConfirm={removeSamples}
            trigger={<Button className={`${btnSecondary} w-full sm:w-auto`} data-testid="remove-sample-content-button"><Trash2 className="h-4 w-4" /> Remove all sample content</Button>}
          />
        </Panel>
      )}
    </AdminLayout>
  );
}
